import React, { useState, useMemo } from 'react';
import {
  type HerxCalendarItem,
  type CalendarViewMode,
  type CalendarItemSource,
  type CalendarCommand,
} from '../../plugins/calendar/calendar-types.js';
import {
  CalendarAdapter,
} from '../../plugins/action-planner/calendar/calendar-adapter.js';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  formatTimeRange,
  formatDateHeading,
  isSameDay,
} from '../../plugins/action-planner/calendar/calendar-date-utils.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface UnifiedCalendarViewProps {
  items: readonly HerxCalendarItem[];
  workspaceId?: string;
  onDispatchCommand?: (command: CalendarCommand) => void;
  onSelectItem?: (item: HerxCalendarItem) => void;
  initialViewMode?: CalendarViewMode;
}

export const UnifiedCalendarView: React.FC<UnifiedCalendarViewProps> = ({
  items,
  workspaceId = 'default',
  onDispatchCommand,
  onSelectItem,
  initialViewMode,
}) => {
  const { t, formatDate } = useTranslation();
  const deviceClass = typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'wide' : 'compact';
  const storageKey = `mesh-calendar-view:${workspaceId}:${deviceClass}`;

  const [viewMode, setViewModeState] = useState<CalendarViewMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'agenda' || stored === 'day' || stored === 'week' || stored === 'month') {
        return stored;
      }
    }
    return initialViewMode || (deviceClass === 'compact' ? 'agenda' : 'week');
  });

  const handleSetViewMode = (mode: CalendarViewMode) => {
    setViewModeState(mode);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, mode);
      } catch (err) {
        // ignore localStorage quota errors
      }
    }
  };

  const [currentDateMs, setCurrentDateMs] = useState<number>(Date.now());
  const [selectedSource, setSelectedSource] = useState<CalendarItemSource | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<HerxCalendarItem | null>(null);

  const adapter = useMemo(() => new CalendarAdapter(items as HerxCalendarItem[]), [items]);

  const filteredItems = useMemo(() => {
    let startMs = 0;
    let endMs = Infinity;

    if (viewMode === 'day') {
      startMs = startOfDay(currentDateMs);
      endMs = endOfDay(currentDateMs);
    } else if (viewMode === 'week') {
      startMs = startOfWeek(currentDateMs);
      endMs = endOfWeek(currentDateMs);
    } else if (viewMode === 'month') {
      startMs = startOfMonth(currentDateMs);
      endMs = endOfMonth(currentDateMs);
    }

    return adapter.filterItems({
      sources: selectedSource === 'all' ? undefined : [selectedSource],
      searchQuery,
      timeRange: viewMode !== 'agenda' ? { startMs, endMs } : undefined,
    });
  }, [adapter, viewMode, currentDateMs, selectedSource, searchQuery]);

  const handleNavigate = (delta: number) => {
    const d = new Date(currentDateMs);
    if (viewMode === 'day') d.setDate(d.getDate() + delta);
    else if (viewMode === 'week') d.setDate(d.getDate() + delta * 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() + delta);
    setCurrentDateMs(d.getTime());
  };

  const handleToday = () => setCurrentDateMs(Date.now());

  // Week days calculation
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDateMs);
    const days: number[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(day.getDate() + i);
      days.push(day.getTime());
    }
    return days;
  }, [currentDateMs]);

  // Color mapper helper using semantic theme tokens
  const getDomainColor = (colorKey: HerxCalendarItem['colorKey'], isOverdue?: boolean) => {
    if (isOverdue) {
      return {
        bg: 'var(--theme-status-danger-bg)',
        border: 'var(--theme-status-danger-text)',
        text: 'var(--theme-status-danger-text)',
        badge: 'var(--theme-status-danger-text)',
      };
    }
    if (colorKey === 'crm') {
      return {
        bg: 'var(--theme-accent-subtle)',
        border: 'var(--theme-accent)',
        text: 'var(--theme-accent)',
        badge: 'var(--theme-accent)',
      };
    }
    if (colorKey === 'appointment') {
      return {
        bg: 'var(--theme-status-success-bg)',
        border: 'var(--theme-status-success-text)',
        text: 'var(--theme-status-success-text)',
        badge: 'var(--theme-status-success-text)',
      };
    }
    if (colorKey === 'scrum') {
      return {
        bg: 'var(--theme-status-warning-bg)',
        border: 'var(--theme-status-warning-text)',
        text: 'var(--theme-status-warning-text)',
        badge: 'var(--theme-status-warning-text)',
      };
    }
    return {
      bg: 'var(--theme-accent-subtle)',
      border: 'var(--theme-accent)',
      text: 'var(--theme-accent)',
      badge: 'var(--theme-accent)',
    };
  };

  return (
    <div
      role="region"
      aria-label={t('calendar.title') || 'Universal Shared Calendar'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--theme-bg-main)',
        color: 'var(--theme-text-primary)',
        borderRadius: 'var(--theme-radius-lg, 12px)',
        border: '1px solid var(--theme-border-subtle)',
        overflow: 'hidden',
      }}
    >
      {/* Calendar Top Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--theme-bg-header)',
          borderBottom: '1px solid var(--theme-border-subtle)',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        {/* Date Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => handleNavigate(-1)}
            aria-label="Previous period"
            style={{
              background: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-primary)',
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 'var(--theme-text-xs)',
            }}
          >
            ←
          </button>
          <button
            onClick={handleToday}
            aria-label={t('calendar.today') || 'Jump to Today'}
            style={{
              background: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-primary)',
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
            }}
          >
            {t('calendar.today') || 'Today'}
          </button>
          <button
            onClick={() => handleNavigate(1)}
            aria-label="Next period"
            style={{
              background: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-primary)',
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 'var(--theme-text-xs)',
            }}
          >
            →
          </button>

          <span style={{ fontSize: 'var(--theme-text-sm)', fontWeight: 700, marginInlineStart: '8px', color: 'var(--theme-text-primary)' }}>
            {formatDateHeading(currentDateMs)}
          </span>
        </div>

        {/* Source Filters */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--theme-bg-subtle)', padding: '3px', borderRadius: 'var(--theme-radius-md, 8px)' }}>
          {[
            { id: 'all', label: t('calendar.allItems') || 'All Items' },
            { id: 'taskflow', label: t('calendar.tasks') || 'Tasks' },
            { id: 'appointment', label: t('calendar.appointments') || 'Appointments' },
            { id: 'crm-followup', label: t('calendar.crm') || 'CRM' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedSource(tab.id as any)}
              aria-label={`Filter by ${tab.label}`}
              style={{
                background: selectedSource === tab.id ? 'var(--theme-accent)' : 'transparent',
                border: 'none',
                color: selectedSource === tab.id ? 'var(--theme-text-inverse, #fff)' : 'var(--theme-text-secondary)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                padding: '4px 8px',
                fontSize: 'var(--theme-text-xs)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* View Mode Toggle */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--theme-bg-subtle)', padding: '3px', borderRadius: 'var(--theme-radius-md, 8px)', border: '1px solid var(--theme-border-subtle)' }}>
          {[
            { id: 'day', label: t('calendar.day') || 'Day' },
            { id: 'week', label: t('calendar.week') || 'Week' },
            { id: 'month', label: t('calendar.month') || 'Month' },
            { id: 'agenda', label: t('calendar.agenda') || 'Agenda' },
          ].map((mode) => (
            <button
              key={mode.id}
              id={`calendar-viewmode-${mode.id}`}
              onClick={() => handleSetViewMode(mode.id as CalendarViewMode)}
              aria-label={`Switch to ${mode.label} view`}
              style={{
                background: viewMode === mode.id ? 'var(--theme-accent-subtle)' : 'transparent',
                border: viewMode === mode.id ? '1px solid var(--theme-accent)' : '1px solid transparent',
                color: viewMode === mode.id ? 'var(--theme-accent)' : 'var(--theme-text-muted)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                padding: '4px 8px',
                fontSize: 'var(--theme-text-xs)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Calendar Viewport */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', height: '100%', minHeight: '400px' }}>
            {weekDays.map((dayMs) => {
              const dayItems = filteredItems.filter((i) => isSameDay(i.startAt || i.dueAt || 0, dayMs));
              const isCurrentDay = isSameDay(dayMs, Date.now());

              return (
                <div
                  key={dayMs}
                  style={{
                    background: isCurrentDay ? 'var(--theme-accent-subtle)' : 'var(--theme-bg-card)',
                    border: isCurrentDay ? '1px solid var(--theme-accent)' : '1px solid var(--theme-border-subtle)',
                    borderRadius: 'var(--theme-radius-md, 8px)',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: isCurrentDay ? 'var(--theme-accent)' : 'var(--theme-text-secondary)', borderBottom: '1px solid var(--theme-border-subtle)', paddingBottom: '4px', textAlign: 'center' }}>
                    {formatDateHeading(dayMs)}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, overflowY: 'auto' }}>
                    {dayItems.length === 0 ? (
                      <div style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)', textAlign: 'center', marginTop: '12px' }}>No items</div>
                    ) : (
                      dayItems.map((item) => {
                        const style = getDomainColor(item.colorKey, item.isOverdue);
                        return (
                          <div
                            key={item.eventId}
                            tabIndex={0}
                            role="button"
                            aria-label={`${item.title} (${item.source})`}
                            onClick={() => {
                              setSelectedItem(item);
                              if (onSelectItem) onSelectItem(item);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedItem(item);
                                if (onSelectItem) onSelectItem(item);
                              }
                            }}
                            style={{
                              background: style.bg,
                              border: `1px solid ${style.border}`,
                              borderRadius: 'var(--theme-radius-sm, 4px)',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: style.text, textTransform: 'uppercase' }}>
                                {item.source === 'crm-followup' ? 'CRM' : item.source === 'appointment' ? 'MEET' : 'TASK'}
                              </span>
                              {item.isOverdue && (
                                <span style={{ fontSize: 'var(--theme-text-xs)', background: 'var(--theme-status-danger-bg)', color: 'var(--theme-status-danger-text)', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>
                                  OVERDUE
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 600, color: 'var(--theme-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <bdi>{item.title}</bdi>
                            </div>
                            {item.startAt && item.endAt && !item.allDay && (
                              <div style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)' }}>
                                {formatTimeRange(item.startAt, item.endAt, item.timeZone)}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* MONTH VIEW */}
        {viewMode === 'month' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center', fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-muted)' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} style={{ padding: '4px' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
              {weekDays.map((dayMs) => {
                const dayItems = filteredItems.filter((i) => isSameDay(i.startAt || i.dueAt || 0, dayMs));
                return (
                  <div
                    key={dayMs}
                    style={{
                      minHeight: '80px',
                      background: 'var(--theme-bg-card)',
                      border: '1px solid var(--theme-border-subtle)',
                      borderRadius: 'var(--theme-radius-sm, 4px)',
                      padding: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <div style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-secondary)', textAlign: 'right' }}>
                      {new Date(dayMs).getDate()}
                    </div>
                    {dayItems.slice(0, 3).map((item) => (
                      <div
                        key={item.eventId}
                        tabIndex={0}
                        role="button"
                        aria-label={item.title}
                        onClick={() => {
                          setSelectedItem(item);
                          if (onSelectItem) onSelectItem(item);
                        }}
                        style={{
                          fontSize: 'var(--theme-text-xs)',
                          background: 'var(--theme-accent-subtle)',
                          color: 'var(--theme-accent)',
                          padding: '2px 4px',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                      >
                        <bdi>{item.title}</bdi>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* AGENDA / DAY VIEW */}
        {(viewMode === 'agenda' || viewMode === 'day') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '700px', margin: '0 auto' }}>
            {filteredItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--theme-text-muted)', fontSize: 'var(--theme-text-sm)' }}>
                No events or tasks scheduled for this period.
              </div>
            ) : (
              filteredItems.map((item) => {
                const style = getDomainColor(item.colorKey, item.isOverdue);
                return (
                  <div
                    key={item.eventId}
                    tabIndex={0}
                    role="button"
                    aria-label={`${item.title} (${item.source})`}
                    onClick={() => {
                      setSelectedItem(item);
                      if (onSelectItem) onSelectItem(item);
                    }}
                    style={{
                      background: 'var(--theme-bg-card)',
                      border: `1px solid ${style.border}`,
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      padding: '12px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 'var(--theme-text-sm)', fontWeight: 600, color: 'var(--theme-text-primary)' }}>
                        <bdi>{item.title}</bdi>
                      </div>
                      <div style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)', marginTop: '2px' }}>
                        Source: {item.source.toUpperCase()} {item.startAt ? `• ${formatDate(item.startAt)}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        aria-label={`Reschedule ${item.title} +1 Day`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const delta = 86400000;
                          if (item.source === 'appointment') {
                            onDispatchCommand?.({
                              type: 'RESCHEDULE_APPOINTMENT',
                              appointmentId: item.sourceId,
                              startAt: (item.startAt || Date.now()) + delta,
                              endAt: (item.endAt || Date.now() + 3600000) + delta,
                              timeZone: item.timeZone || 'UTC',
                            });
                          } else if (item.source === 'crm-followup') {
                            onDispatchCommand?.({
                              type: 'UPDATE_FOLLOWUP_SCHEDULE',
                              followUpId: item.sourceId,
                              scheduledAt: (item.startAt || Date.now()) + delta,
                              dueAt: (item.dueAt || item.startAt || Date.now()) + delta,
                            });
                          } else {
                            onDispatchCommand?.({
                              type: 'RESCHEDULE_TASK',
                              taskId: item.sourceId,
                              dueAt: (item.dueAt || Date.now()) + delta,
                            });
                          }
                        }}
                        style={{
                          fontSize: 'var(--theme-text-xs)',
                          background: 'var(--theme-bg-subtle)',
                          border: '1px solid var(--theme-border)',
                          color: 'var(--theme-text-secondary)',
                          borderRadius: 'var(--theme-radius-sm, 4px)',
                          padding: '3px 8px',
                          cursor: 'pointer',
                        }}
                      >
                        +1 Day
                      </button>
                      {item.isOverdue && (
                        <span style={{ fontSize: 'var(--theme-text-xs)', background: 'var(--theme-status-danger-bg)', color: 'var(--theme-status-danger-text)', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
